import React from 'react';
import { Table, Button } from 'react-bootstrap';
import { DailyReportResponseV4, DailyReportDataV4 } from '../../../../services/cm-report/response/DailyReportResponseV4';
import { formatPrice } from '../../../../shared/utils/formatPrice';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { IconArrowDown, IconArrowUp } from '@tabler/icons-react';

type SortDirection = "asc" | "desc";
type OrderByOption = "date" | "rooms" | "room_sold" | "daily_revenue" | "revpar" | "occupancy" | "available";

interface DailyReportTableV4Props {
    data: DailyReportResponseV4;
    sortConfig: { orderBy: string; sort: SortDirection };
    onSort: (orderBy: string) => void;
}

const DailyReportTableV4: React.FC<DailyReportTableV4Props> = ({ data, sortConfig, onSort }) => {
    // Updated columns to match V4 API response fields
    const columns = [
        { label: "Date", key: "date", sortKey: "date" },
        { label: "Rooms", key: "rooms", sortKey: "rooms" },
        { label: "Room Sold", key: "room_sold", sortKey: "room_sold" },
        { label: "Daily Revenue", key: "daily_revenue", sortKey: "daily_revenue" },
        { label: "RevPar", key: "revpar", sortKey: "revpar" },
        { label: "Occupancy (%)", key: "occupancy", sortKey: "occupancy" },
        { label: "Available", key: "available", sortKey: "available" },
    ];

    const getSortIcon = (columnKey: OrderByOption) => {
        if (sortConfig.orderBy !== columnKey) {
            return null;
        }
        return sortConfig.sort === 'asc' ? <IconArrowUp size={14} /> : <IconArrowDown size={14} />;
    };

    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Daily Report", 14, 10);

        const tableData = data.data.map(row => columns.map(col => {
            const value = row[col.key as keyof DailyReportDataV4];
            if (col.key === 'daily_revenue' || col.key === 'revpar') {
                return formatPrice(Number(value));
            } else if (col.key === 'occupancy') {
                return `${value}%`;
            }
            return value;
        }));

        autoTable(doc, {
            head: [columns.map(col => col.label)],
            body: tableData,
        });

        doc.save("DailyReportV4.pdf");
    };

    const exportToExcel = () => {
        const wsData = [
            columns.map(col => col.label),
            ...data.data.map(row => columns.map(col => {
                const value = row[col.key as keyof DailyReportDataV4];
                if (col.key === 'daily_revenue' || col.key === 'revpar') {
                    return formatPrice(Number(value));
                } else if (col.key === 'occupancy') {
                    return `${value}%`;
                }
                return value;
            })),
            // Updated total row to match V4 fields
            ["Total", data.total.rooms, data.total.room_sold, formatPrice(data.total.daily_revenue), formatPrice(data.total.revpar), `${data.total.occupancy}%`, data.total.available]
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Daily Report V4");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(blob, "DailyReportV4.xlsx");
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>

            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    onClick={() => onSort(col.sortKey)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="d-flex align-items-center justify-content-between">
                                        {col.label}
                                        {getSortIcon(col.sortKey as OrderByOption)}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data?.data?.length > 0 ? (
                            data.data.map((row: DailyReportDataV4, rowIndex) => (
                                <tr key={rowIndex}>
                                    {columns.map((col, colIndex) => (
                                        <td key={colIndex}>
                                            {
                                                col.key === 'daily_revenue' || col.key === 'revpar' ? formatPrice(Number(row[col.key as keyof DailyReportDataV4]))
                                                    : col.key === 'occupancy' ? `${row[col.key as keyof DailyReportDataV4]}%`
                                                        : row[col.key as keyof DailyReportDataV4]
                                            }
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="text-center">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td><strong>Total</strong></td>
                            <td><strong>{data.total.rooms}</strong></td>
                            <td><strong>{data.total.room_sold}</strong></td>
                            <td><strong>{formatPrice(data.total.daily_revenue)}</strong></td>
                            <td><strong>{formatPrice(data.total.revpar)}</strong></td>
                            <td><strong>{data.total.occupancy}%</strong></td>
                            <td><strong>{data.total.available}</strong></td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
};

export default DailyReportTableV4;